import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaTimes, 
  FaHome, 
  FaMapMarkerAlt, 
  FaRupeeSign,
  FaBed,
  FaBath,
  FaUsers,
  FaImage,
  FaPlus,
  FaCamera,
  FaUpload,
  FaTrash,
  FaChevronDown,
  FaChevronUp,
  FaClock,
  FaCheck
} from 'react-icons/fa';
import './AddPropertyModal.css';

import { getApiUrl } from '../config/api';
import { validateOfferDateRange, computeOfferStatus } from '../utils/offerStatus';
import { toast } from 'react-toastify';

const AddPropertyModal = ({ isOpen, onClose, onPropertyAdded }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadingImages, setUploadingImages] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: '',
    type: 'Hotel',
    description: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    pricePerNight: '',
    bedrooms: 1,
    bathrooms: 1,
    beds: 1,
    maxGuests: 2,
    propertySizeSqFt: '',
    // Check-in & Check-out (like similar apps)
    checkInTime: '3:00 PM - 11:00 PM',
    checkOutTime: '11:00 AM',
    minStayNights: 1,
    maxStayNights: '',
    securityDeposit: 0,
    amenities: [],
    houseRules: [],
    images: [], // Array of File objects for preview
    customAmenities: [], // Custom amenities added by user
    customHouseRules: [], // Custom house rules added by user
    nearbyPlaces: [], // Nearby places with manual entry
    touristPlaces: [], // Tourist places with image upload
    // Policy fields
    cancellationPolicy: 'Free cancellation up to 24 hours before check-in',
    paymentPolicy: 'Pay at property',
    additionalNotes: '',
    // Discount/Offer fields
    discountIsActive: false,
    discountType: 'percentage',
    discountValue: '',
    discountTitle: '',
    discountDescription: '',
    discountValidFrom: '',
    discountValidUntil: '',
    discountMinStayNights: 1,
    discountMaxDiscountAmount: '',
    discountBadge: 'Special Offer',
    discountTermsAndConditions: '',
    cleaningFee: 0,
    serviceFeePercent: 10,
  });

  const [showAddAmenity, setShowAddAmenity] = useState(false);
  const [showCancellationDropdown, setShowCancellationDropdown] = useState(false);
  const cancellationBtnRef = useRef(null);
  const [showPaymentDropdown, setShowPaymentDropdown] = useState(false);
  const paymentBtnRef = useRef(null);
  const [newAmenity, setNewAmenity] = useState('');
  const [showAddHouseRule, setShowAddHouseRule] = useState(false);
  const [newHouseRule, setNewHouseRule] = useState('');
  
  // Nearby Places state
  const [showAddNearbyPlace, setShowAddNearbyPlace] = useState(false);
  const [newNearbyPlace, setNewNearbyPlace] = useState({
    name: '',
    category: '',
    distance: '',
    unit: 'km'
  });
  
  // Tourist Places state
  const [showAddTouristPlace, setShowAddTouristPlace] = useState(false);
  const [newTouristPlace, setNewTouristPlace] = useState({
    name: '',
    city: '',
    state: '',
    distance: '',
    description: '',
    image: null
  });
  
  // Collapsible sections state
  const [showAmenitiesSection, setShowAmenitiesSection] = useState(false);
  const [showHouseRulesSection, setShowHouseRulesSection] = useState(false);

  const propertyTypes = ['Hotel', 'Villa', 'Homestay', 'PG / Hostel', 'Resort', 'Apartment', 'Guesthouse', 'Other'];
  
  // Comprehensive amenities list - all facilities
  const allAmenitiesList = [
    // Basic Amenities
    'WiFi',
    'Air Conditioning',
    'Heating',
    'TV',
    'Kitchen',
    'Washing Machine',
    'Dryer',
    'Iron',
    
    // Bathroom
    'Hot Water',
    'Toiletries',
    'Hair Dryer',
    'Towels',
    'Bathtub',
    
    // Bedroom & Living
    'Bed Linens',
    'Extra Pillows',
    'Hangers',
    'Wardrobe',
    'Sofa',
    'Dining Table',
    'Work Desk',
    
    // Kitchen & Dining
    'Refrigerator',
    'Microwave',
    'Oven',
    'Stove',
    'Dishwasher',
    'Coffee Maker',
    'Kettle',
    'Utensils',
    'Dishes & Silverware',
    
    // Outdoor
    'Balcony',
    'Terrace',
    'Garden',
    'Patio',
    'BBQ Grill',
    'Outdoor Furniture',
    
    // Parking & Access
    'Free Parking',
    'Paid Parking',
    'Street Parking',
    'Lift/Elevator',
    'Wheelchair Accessible',
    
    // Safety & Security
    'CCTV',
    'Security Guard',
    'Fire Extinguisher',
    'Smoke Detector',
    'First Aid Kit',
    'Safe/Locker',
    
    // Entertainment
    'Cable TV',
    'Netflix',
    'Amazon Prime',
    'Gaming Console',
    'Books',
    'Board Games',
    
    // Services
    'Room Service',
    'Housekeeping',
    'Laundry Service',
    'Breakfast Included',
    'Restaurant',
    'Gym',
    'Swimming Pool',
    'Spa',
    'Concierge',
    
    // Family Friendly
    'Crib',
    'High Chair',
    'Baby Bath',
    'Children\'s Books',
    'Kids Toys',
    
    // Pet Friendly
    'Pets Allowed',
    'Pet Bowls',
    'Pet Bed',
    
    // Business
    'Dedicated Workspace',
    'Fast WiFi (100+ Mbps)',
    'Printer',
    'Meeting Room',
    
    // Others
    'Power Backup',
    'Water Purifier',
    'Geyser',
    'Fan',
    'Mosquito Net',
    'Intercom',
    'Doorman'
  ];

  // Comprehensive house rules list
  const allHouseRulesList = [
    // Timing Rules
    'Check-in: 2 PM - 10 PM',
    'Check-out: 11 AM',
    'Quiet Hours: 10 PM - 8 AM',
    
    // Guest Rules
    'No Smoking',
    'No Pets',
    'No Parties/Events',
    'No Unregistered Guests',
    'Maximum Guests Limit',
    
    // Behavior Rules
    'Respect Neighbors',
    'Keep Property Clean',
    'No Loud Music',
    'No Illegal Activities',
    
    // Safety Rules
    'ID Proof Required',
    'No Candles/Incense',
    'No Cooking in Bedroom',
    'Emergency Exit Awareness',
    
    // Damage & Liability
    'Damage Charges Apply',
    'Security Deposit Required',
    'Report Damages Immediately',
    
    // Usage Rules
    'No Commercial Use',
    'No Long-term Storage',
    'Conserve Water & Electricity',
    
    // Special Rules
    'Shoes Off Inside',
    'No Outside Food in Pool Area',
    'Parking in Designated Area Only',
    'Dispose Garbage Properly'
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleAmenityToggle = (amenity) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter(a => a !== amenity)
        : [...prev.amenities, amenity]
    }));
  };

  const handleAddCustomAmenity = () => {
    if (newAmenity && newAmenity.trim()) {
      setFormData(prev => ({
        ...prev,
        customAmenities: [...prev.customAmenities, newAmenity.trim()]
      }));
      setNewAmenity('');
      setShowAddAmenity(false);
    }
  };

  const handleCancelAddAmenity = () => {
    setNewAmenity('');
    setShowAddAmenity(false);
  };

  const handleRemoveCustomAmenity = (index) => {
    setFormData(prev => ({
      ...prev,
      customAmenities: prev.customAmenities.filter((_, i) => i !== index)
    }));
  };

  const handleHouseRuleToggle = (rule) => {
    setFormData(prev => ({
      ...prev,
      houseRules: prev.houseRules.includes(rule)
        ? prev.houseRules.filter(r => r !== rule)
        : [...prev.houseRules, rule]
    }));
  };

  const handleAddCustomHouseRule = () => {
    if (newHouseRule && newHouseRule.trim()) {
      setFormData(prev => ({
        ...prev,
        customHouseRules: [...prev.customHouseRules, newHouseRule.trim()]
      }));
      setNewHouseRule('');
      setShowAddHouseRule(false);
    }
  };

  const handleCancelAddHouseRule = () => {
    setNewHouseRule('');
    setShowAddHouseRule(false);
  };

  const handleRemoveCustomHouseRule = (index) => {
    setFormData(prev => ({
      ...prev,
      customHouseRules: prev.customHouseRules.filter((_, i) => i !== index)
    }));
  };

  // Nearby Places handlers
  const handleAddNearbyPlace = () => {
    if (newNearbyPlace.name && newNearbyPlace.distance) {
      setFormData(prev => ({
        ...prev,
        nearbyPlaces: [...prev.nearbyPlaces, { ...newNearbyPlace, distance: parseFloat(newNearbyPlace.distance) }]
      }));
      setNewNearbyPlace({ name: '', category: '', distance: '', unit: 'km' });
      setShowAddNearbyPlace(false);
    }
  };

  const handleRemoveNearbyPlace = (index) => {
    setFormData(prev => ({
      ...prev,
      nearbyPlaces: prev.nearbyPlaces.filter((_, i) => i !== index)
    }));
  };

  // Tourist Places handlers
  const handleAddTouristPlace = () => {
    if (newTouristPlace.name && newTouristPlace.city && newTouristPlace.state) {
      setFormData(prev => ({
        ...prev,
        touristPlaces: [
          ...prev.touristPlaces,
          {
            ...newTouristPlace,
            distance: newTouristPlace.distance ? parseFloat(newTouristPlace.distance) : undefined
          }
        ]
      }));
      setNewTouristPlace({ name: '', city: '', state: '', distance: '', description: '', image: null });
      setShowAddTouristPlace(false);
    }
  };

  const handleRemoveTouristPlace = (index) => {
    setFormData(prev => ({
      ...prev,
      touristPlaces: prev.touristPlaces.filter((_, i) => i !== index)
    }));
  };

  const handleTouristPlaceImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (validTypes.includes(file.type)) {
        setNewTouristPlace(prev => ({ ...prev, image: file }));
      } else {
        toast.warning('Only JPG, PNG, and WEBP images are allowed.');
      }
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => {
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      return validTypes.includes(file.type);
    });

    if (validFiles.length !== files.length) {
      setError('Some files were skipped. Only JPG, PNG, and WEBP images are allowed.');
    }

    if (validFiles.length > 0) {
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...validFiles]
      }));
    }

    // Reset input
    e.target.value = '';
  };

  const handleCameraCapture = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...files]
      }));
    }
    // Reset input
    e.target.value = '';
  };

  const handleImageRemove = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.name.trim()) {
      setError('Property name is required');
      return;
    }
    if (!formData.city.trim()) {
      setError('City is required');
      return;
    }
    if (!formData.pricePerNight || formData.pricePerNight <= 0) {
      setError('Valid price per night is required');
      return;
    }
    if (formData.images.length === 0) {
      setError('At least one image is required');
      return;
    }

    setLoading(true);
    setUploadingImages(true);

    try {
      const token = localStorage.getItem('token');
      
      // Prepare FormData for multipart/form-data
      const submitFormData = new FormData();
      submitFormData.append('name', formData.name);
      submitFormData.append('type', formData.type);
      submitFormData.append('description', formData.description || '');
      submitFormData.append('address', formData.address || '');
      submitFormData.append('city', formData.city);
      submitFormData.append('state', formData.state || '');
      submitFormData.append('pincode', formData.pincode || '');
      submitFormData.append('pricePerNight', formData.pricePerNight);
      submitFormData.append('cleaningFee', formData.cleaningFee || 0);
      submitFormData.append('serviceFeePercent', formData.serviceFeePercent || 10);
      submitFormData.append('securityDeposit', formData.securityDeposit || 0);
      submitFormData.append('bedrooms', formData.bedrooms);
      submitFormData.append('bathrooms', formData.bathrooms);
      submitFormData.append('beds', formData.beds);
      submitFormData.append('maxGuests', formData.maxGuests);
      submitFormData.append('propertySizeSqFt', formData.propertySizeSqFt || '');
      submitFormData.append('checkInTime', formData.checkInTime || '3:00 PM - 11:00 PM');
      submitFormData.append('checkOutTime', formData.checkOutTime || '11:00 AM');
      submitFormData.append('minStayNights', formData.minStayNights || 1);
      submitFormData.append('maxStayNights', formData.maxStayNights || '');
      
      // Combine standard and custom amenities
      const allAmenities = [...formData.amenities, ...formData.customAmenities];
      submitFormData.append('amenities', JSON.stringify(allAmenities));
      
      // Combine selected house rules and custom rules
      const allHouseRules = [...formData.houseRules, ...formData.customHouseRules];
      submitFormData.append('houseRules', JSON.stringify(allHouseRules));
      
      // Append images
      formData.images.forEach((image, index) => {
        submitFormData.append('images', image);
      });

      // Append nearby places
      submitFormData.append('nearbyPlaces', JSON.stringify(formData.nearbyPlaces));

      // Append tourist places data (without images first)
      const touristPlacesData = formData.touristPlaces.map(tp => ({
        name: tp.name,
        city: tp.city,
        state: tp.state,
        distance: tp.distance,
        description: tp.description,
        image: '' // Will be filled by backend
      }));
      submitFormData.append('touristPlaces', JSON.stringify(touristPlacesData));

      // Append tourist place images separately
      formData.touristPlaces.forEach((tp, index) => {
        if (tp.image) {
          submitFormData.append('touristPlaceImages', tp.image);
        }
      });

      // Append policy data
      submitFormData.append('policies', JSON.stringify({
        cancellationPolicy: formData.cancellationPolicy,
        paymentPolicy: formData.paymentPolicy,
        additionalNotes: formData.additionalNotes
      }));

      // Append discount data
      if (formData.discountIsActive) {
        const dateCheck = validateOfferDateRange(formData.discountValidFrom, formData.discountValidUntil);
        if (!dateCheck.valid) {
          toast.error(dateCheck.message);
          setLoading(false);
          return;
        }
        const offerStatus = computeOfferStatus({
          startDate: formData.discountValidFrom,
          endDate: formData.discountValidUntil,
        });
        submitFormData.append('discount', JSON.stringify({
          isActive: offerStatus === 'active',
          status: offerStatus,
          type: formData.discountType,
          value: parseFloat(formData.discountValue) || 0,
          title: formData.discountTitle,
          description: formData.discountDescription,
          validFrom: formData.discountValidFrom ? new Date(formData.discountValidFrom) : null,
          validUntil: formData.discountValidUntil ? new Date(formData.discountValidUntil) : null,
          minStayNights: formData.discountMinStayNights,
          maxDiscountAmount: formData.discountMaxDiscountAmount ? parseFloat(formData.discountMaxDiscountAmount) : null,
          badge: formData.discountBadge,
          termsAndConditions: formData.discountTermsAndConditions
        }));
      }

      console.log('Submitting property data:', {
        name: formData.name,
        nearbyPlacesCount: formData.nearbyPlaces.length,
        touristPlacesCount: formData.touristPlaces.length,
        imagesCount: formData.images.length
      });

      const response = await fetch(getApiUrl('/api/properties/create'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          // Don't set Content-Type, let browser set it with boundary
        },
        body: submitFormData,
      });

      const result = await response.json();

      if (result.success) {
        // Reset form
        setFormData({
          name: '',
          type: 'Hotel',
          description: '',
          address: '',
          city: '',
          state: '',
          pincode: '',
          pricePerNight: '',
          bedrooms: 1,
          bathrooms: 1,
          beds: 1,
          maxGuests: 2,
          propertySizeSqFt: '',
          checkInTime: '3:00 PM - 11:00 PM',
          checkOutTime: '11:00 AM',
          minStayNights: 1,
          maxStayNights: '',
          securityDeposit: 0,
          amenities: [],
          houseRules: [],
          images: [],
          customAmenities: [],
          customHouseRules: [],
          nearbyPlaces: [],
          touristPlaces: [],
          cancellationPolicy: 'Free cancellation up to 24 hours before check-in',
          paymentPolicy: 'Pay at property',
          additionalNotes: '',
          discountIsActive: false,
          discountType: 'percentage',
          discountValue: '',
          discountTitle: '',
          discountDescription: '',
          discountValidFrom: '',
          discountValidUntil: '',
          discountMinStayNights: 1,
          discountMaxDiscountAmount: '',
          discountBadge: 'Special Offer',
          discountTermsAndConditions: '',
          cleaningFee: 0,
          serviceFeePercent: 10,
        });
       
        
        onPropertyAdded(result.property);
        onClose();
      } else {
        console.error('Server error:', result);
        setError(result.message || result.error || 'Failed to create property');
      }
    } catch (err) {
      console.error('Create property error:', err);
      setError('Failed to create property. Please try again.');
    } finally {
      setLoading(false);
      setUploadingImages(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="modal-overlay" onClick={onClose}>
        <motion.div
          className="add-property-modal"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-header">
            <h2>Add New Property</h2>
            <button className="close-button" onClick={onClose}>
              <FaTimes />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="property-form">
            {error && <div className="error-message">{error}</div>}

            <div className="form-section">
              <h3><FaHome /> Basic Information</h3>
              
              <div className="form-group">
                <label>Property Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="e.g., Cozy 2BHK Apartment in Bandra"
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Property Type *</label>
                  <select
                    value={formData.type}
                    onChange={(e) => handleInputChange('type', e.target.value)}
                    required
                  >
                    {propertyTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>City *</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    placeholder="e.g., Mumbai"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>State / Region</label>
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) => handleInputChange('state', e.target.value)}
                    placeholder="e.g., Maharashtra, Karnataka"
                  />
                </div>
                <div className="form-group">
                  <label>Pincode / Zipcode</label>
                  <input
                    type="text"
                    value={formData.pincode}
                    onChange={(e) => handleInputChange('pincode', e.target.value)}
                    placeholder="e.g., 560001"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Address</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  placeholder="Full address"
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Describe your property..."
                  rows={4}
                />
              </div>
            </div>

            <div className="form-section">
              <h3><FaBed /> Property Details</h3>
              
              <div className="form-row">
                <div className="form-group">
                  <label><FaBed /> Bedrooms</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.bedrooms}
                    onChange={(e) => handleInputChange('bedrooms', parseInt(e.target.value) || 0)}
                  />
                </div>

                <div className="form-group">
                  <label><FaBath /> Bathrooms</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.bathrooms}
                    onChange={(e) => handleInputChange('bathrooms', parseInt(e.target.value) || 1)}
                  />
                </div>

                <div className="form-group">
                  <label><FaUsers /> Max Guests</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.maxGuests}
                    onChange={(e) => handleInputChange('maxGuests', parseInt(e.target.value) || 1)}
                  />
                </div>
                <div className="form-group">
                  <label><FaBed /> Beds</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.beds}
                    onChange={(e) => handleInputChange('beds', parseInt(e.target.value) || 1)}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Property Size (sq ft)</label>
                <input
                  type="number"
                  min="0"
                  value={formData.propertySizeSqFt}
                  onChange={(e) => handleInputChange('propertySizeSqFt', e.target.value)}
                  placeholder="e.g., 1200 (optional)"
                />
              </div>
            </div>

            <div className="form-section">
              <h3><FaClock /> Check-in & Check-out</h3>
              <p className="section-description">Set when guests can check in and check out (like Airbnb, Booking.com)</p>
              <div className="form-row">
                <div className="form-group">
                  <label>Check-in window</label>
                  <select
                    value={formData.checkInTime}
                    onChange={(e) => handleInputChange('checkInTime', e.target.value)}
                  >
                    <option value="2:00 PM - 10:00 PM">2:00 PM - 10:00 PM</option>
                    <option value="3:00 PM - 11:00 PM">3:00 PM - 11:00 PM</option>
                    <option value="12:00 PM - 10:00 PM">12:00 PM - 10:00 PM</option>
                    <option value="Anytime after 2:00 PM">Anytime after 2:00 PM</option>
                    <option value="Anytime after 3:00 PM">Anytime after 3:00 PM</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Check-out by</label>
                  <select
                    value={formData.checkOutTime}
                    onChange={(e) => handleInputChange('checkOutTime', e.target.value)}
                  >
                    <option value="10:00 AM">10:00 AM</option>
                    <option value="11:00 AM">11:00 AM</option>
                    <option value="12:00 PM">12:00 PM</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Minimum stay (nights)</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.minStayNights}
                    onChange={(e) => handleInputChange('minStayNights', parseInt(e.target.value) || 1)}
                  />
                </div>
                <div className="form-group">
                  <label>Maximum stay (nights)</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.maxStayNights}
                    onChange={(e) => handleInputChange('maxStayNights', e.target.value)}
                    placeholder="Leave empty for no limit"
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3><FaRupeeSign /> Pricing</h3>
              
              <div className="form-group">
                <label>Price per Night (₹) *</label>
                <input
                  type="number"
                  min="0"
                  value={formData.pricePerNight}
                  onChange={(e) => handleInputChange('pricePerNight', parseFloat(e.target.value) || '')}
                  placeholder="e.g., 2500"
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Cleaning Fee (₹)</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.cleaningFee}
                    onChange={(e) => handleInputChange('cleaningFee', parseFloat(e.target.value) || 0)}
                    placeholder="e.g., 200"
                  />
                  <span className="field-hint">One-time fee per booking</span>
                </div>
                <div className="form-group">
                  <label>Service Fee (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="50"
                    step="1"
                    value={formData.serviceFeePercent}
                    onChange={(e) => handleInputChange('serviceFeePercent', parseFloat(e.target.value) || 10)}
                    placeholder="10"
                  />
                  <span className="field-hint">% of room price</span>
                </div>
                <div className="form-group">
                  <label>Security Deposit (₹)</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.securityDeposit}
                    onChange={(e) => handleInputChange('securityDeposit', parseFloat(e.target.value) || 0)}
                    placeholder="e.g., 2000 (optional)"
                  />
                  <span className="field-hint">Refundable deposit if applicable</span>
                </div>
              </div>
            </div>

            <div className="form-section">
              <div 
                className="collapsible-header"
                onClick={() => setShowAmenitiesSection(!showAmenitiesSection)}
              >
                <h3>Amenities & Facilities</h3>
                {showAmenitiesSection ? <FaChevronUp /> : <FaChevronDown />}
              </div>
              
              {showAmenitiesSection && (
                <>
                  <p className="section-description">Select all amenities available at your property</p>
                  
                  <div className="amenities-grid">
                    {allAmenitiesList.map(amenity => (
                      <label key={amenity} className="amenity-checkbox">
                        <input
                          type="checkbox"
                          checked={formData.amenities.includes(amenity)}
                          onChange={() => handleAmenityToggle(amenity)}
                        />
                        <span>{amenity}</span>
                      </label>
                    ))}
                    
                    {formData.customAmenities.map((amenity, index) => (
                      <label key={`custom-${index}`} className="amenity-checkbox custom-amenity">
                        <input
                          type="checkbox"
                          checked
                          readOnly
                        />
                        <span>{amenity}</span>
                        <button
                          type="button"
                          className="remove-custom"
                          onClick={() => handleRemoveCustomAmenity(index)}
                          title="Remove custom amenity"
                        >
                          <FaTrash />
                        </button>
                      </label>
                    ))}
                  </div>
                  
                  {showAddAmenity ? (
                    <div className="add-amenity-input-container">
                      <input
                        type="text"
                        value={newAmenity}
                        onChange={(e) => setNewAmenity(e.target.value)}
                        placeholder="Enter custom amenity (e.g., Dishwasher, Fireplace)"
                        className="add-amenity-input"
                        autoFocus
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddCustomAmenity();
                          }
                        }}
                      />
                      <div className="add-amenity-buttons">
                        <button
                          type="button"
                          className="btn-confirm-amenity"
                          onClick={handleAddCustomAmenity}
                        >
                          Add
                        </button>
                        <button
                          type="button"
                          className="btn-cancel-amenity"
                          onClick={handleCancelAddAmenity}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="btn-add-more"
                      onClick={() => setShowAddAmenity(true)}
                    >
                      <FaPlus />
                      Add Custom Amenity
                    </button>
                  )}
                  
                  {/* Show selected amenities count */}
                  {formData.amenities.length > 0 && (
                    <div className="selected-count">
                      <strong>{formData.amenities.length + formData.customAmenities.length}</strong> amenities selected
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="form-section">
              <h3><FaImage /> Property Images</h3>
              <p className="section-description">Upload high-quality images to attract guests. First image will be the cover photo.</p>
              
              <div className="images-section">
                {/* Upload zone when no images */}
                {formData.images.length === 0 && (
                  <div 
                    className="image-dropzone"
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('dragover'); }}
                    onDragLeave={(e) => { e.currentTarget.classList.remove('dragover'); }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove('dragover');
                      const files = Array.from(e.dataTransfer.files);
                      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
                      const validFiles = files.filter(f => validTypes.includes(f.type));
                      if (validFiles.length > 0) {
                        setFormData(prev => ({ ...prev, images: [...prev.images, ...validFiles] }));
                      }
                    }}
                  >
                    <div className="dropzone-icon"><FaCamera /></div>
                    <h4>Drag & drop images here</h4>
                    <p>or click to browse from your device</p>
                    <span className="dropzone-formats">JPG, PNG, WEBP supported</span>
                  </div>
                )}

                {/* Image preview grid */}
                {formData.images.length > 0 && (
                  <>
                    <div className="images-preview-grid">
                      {formData.images.map((image, index) => (
                        <div key={index} className={`image-preview-card ${index === 0 ? 'cover-image' : ''}`}>
                          <img 
                            src={URL.createObjectURL(image)} 
                            alt={`Property ${index + 1}`}
                          />
                          <div className="image-overlay">
                            {index === 0 && <span className="cover-badge">★ Cover</span>}
                            <span className="image-number">{index + 1}</span>
                          </div>
                          <button
                            type="button"
                            className="remove-image-btn"
                            onClick={() => handleImageRemove(index)}
                            title="Remove image"
                          >
                            <FaTimes />
                          </button>
                        </div>
                      ))}

                      {/* Add more card */}
                      <div 
                        className="add-more-card"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <FaPlus />
                        <span>Add More</span>
                      </div>
                    </div>

                    <div className="image-upload-actions">
                      <span className="images-count-badge">
                        <FaImage /> {formData.images.length} {formData.images.length === 1 ? 'image' : 'images'} selected
                      </span>
                      <div className="upload-btn-group">
                        <button
                          type="button"
                          className="upload-action-btn"
                          onClick={() => fileInputRef.current?.click()}
                        >
                          <FaUpload /> Upload Files
                        </button>
                        <button
                          type="button"
                          className="upload-action-btn camera-action-btn"
                          onClick={() => cameraInputRef.current?.click()}
                        >
                          <FaCamera /> Camera
                        </button>
                      </div>
                    </div>
                  </>
                )}

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  multiple
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
                <input
                  ref={cameraInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  multiple
                  onChange={handleCameraCapture}
                  style={{ display: 'none' }}
                />
              </div>
            </div>

            <div className="form-section">
              <div 
                className="collapsible-header"
                onClick={() => setShowHouseRulesSection(!showHouseRulesSection)}
              >
                <h3>House Rules</h3>
                {showHouseRulesSection ? <FaChevronUp /> : <FaChevronDown />}
              </div>
              
              {showHouseRulesSection && (
                <>
                  <p className="section-description">Select all rules that apply to your property</p>
                  
                  <div className="house-rules-grid">
                    {allHouseRulesList.map(rule => (
                      <label key={rule} className="house-rule-checkbox">
                        <input
                          type="checkbox"
                          checked={formData.houseRules.includes(rule)}
                          onChange={() => handleHouseRuleToggle(rule)}
                        />
                        <span>{rule}</span>
                      </label>
                    ))}
                    
                    {formData.customHouseRules.map((rule, index) => (
                      <label key={`custom-${index}`} className="house-rule-checkbox custom-rule">
                        <input
                          type="checkbox"
                          checked
                          readOnly
                        />
                        <span>{rule}</span>
                        <button
                          type="button"
                          className="remove-custom"
                          onClick={() => handleRemoveCustomHouseRule(index)}
                          title="Remove custom rule"
                        >
                          <FaTrash />
                        </button>
                      </label>
                    ))}
                  </div>
                  
                  {showAddHouseRule ? (
                    <div className="add-rule-input-container">
                      <input
                        type="text"
                        value={newHouseRule}
                        onChange={(e) => setNewHouseRule(e.target.value)}
                        placeholder="Enter custom house rule (e.g., No drinking, Check-in after 2 PM)"
                        className="add-rule-input"
                        autoFocus
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddCustomHouseRule();
                          }
                        }}
                      />
                      <div className="add-rule-buttons">
                        <button
                          type="button"
                          className="btn-confirm-rule"
                          onClick={handleAddCustomHouseRule}
                        >
                          Add
                        </button>
                        <button
                          type="button"
                          className="btn-cancel-rule"
                          onClick={handleCancelAddHouseRule}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="btn-add-more"
                      onClick={() => setShowAddHouseRule(true)}
                    >
                      <FaPlus />
                      Add Custom Rule
                    </button>
                  )}

                  {/* Show selected rules count */}
                  {(formData.houseRules.length > 0 || formData.customHouseRules.length > 0) && (
                    <div className="selected-count">
                      <strong>{formData.houseRules.length + formData.customHouseRules.length}</strong> rules selected
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="form-section">
              <h3>Nearby Places (Optional)</h3>
              <p className="section-description">Add nearby places like bus stops, railway stations, malls, hospitals, etc.</p>
              
              {formData.nearbyPlaces.length > 0 && (
                <div className="nearby-places-list">
                  {formData.nearbyPlaces.map((place, index) => (
                    <div key={index} className="nearby-place-item">
                      <div className="nearby-place-info">
                        <strong>{place.name}</strong>
                        {place.category && <span className="place-category"> ({place.category})</span>}
                        <span className="place-distance"> - {place.distance} {place.unit}</span>
                      </div>
                      <button
                        type="button"
                        className="remove-custom"
                        onClick={() => handleRemoveNearbyPlace(index)}
                        title="Remove place"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              {showAddNearbyPlace ? (
                <div className="add-nearby-place-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Place Name *</label>
                      <input
                        type="text"
                        value={newNearbyPlace.name}
                        onChange={(e) => setNewNearbyPlace(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="e.g., Central Bus Stop"
                        autoFocus
                      />
                    </div>
                    <div className="form-group">
                      <label>Category</label>
                      <select
                        value={newNearbyPlace.category}
                        onChange={(e) => setNewNearbyPlace(prev => ({ ...prev, category: e.target.value }))}
                      >
                        <option value="">Select category</option>
                        <option value="Bus Stop">Bus Stop</option>
                        <option value="Railway Station">Railway Station</option>
                        <option value="Metro Station">Metro Station</option>
                        <option value="Airport">Airport</option>
                        <option value="Mall">Mall</option>
                        <option value="Hospital">Hospital</option>
                        <option value="School">School</option>
                        <option value="Restaurant">Restaurant</option>
                        <option value="Tourist Place">Tourist Place</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Distance *</label>
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        value={newNearbyPlace.distance}
                        onChange={(e) => setNewNearbyPlace(prev => ({ ...prev, distance: e.target.value }))}
                        placeholder="e.g., 0.5"
                      />
                    </div>
                    <div className="form-group">
                      <label>Unit</label>
                      <select
                        value={newNearbyPlace.unit}
                        onChange={(e) => setNewNearbyPlace(prev => ({ ...prev, unit: e.target.value }))}
                      >
                        <option value="km">Kilometers</option>
                        <option value="meters">Meters</option>
                      </select>
                    </div>
                  </div>
                  <div className="add-amenity-buttons">
                    <button
                      type="button"
                      className="btn-confirm-amenity"
                      onClick={handleAddNearbyPlace}
                    >
                      Add Place
                    </button>
                    <button
                      type="button"
                      className="btn-cancel-amenity"
                      onClick={() => {
                        setShowAddNearbyPlace(false);
                        setNewNearbyPlace({ name: '', category: '', distance: '', unit: 'km' });
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  className="btn-add-more"
                  onClick={() => setShowAddNearbyPlace(true)}
                >
                  <FaPlus />
                  Add Nearby Place
                </button>
              )}
            </div>

            <div className="form-section">
              <h3>Tourist Attractions Nearby (Optional)</h3>
              <p className="section-description">Add tourist places with images, city, state, and description</p>
              
              {formData.touristPlaces.length > 0 && (
                <div className="tourist-places-grid">
                  {formData.touristPlaces.map((place, index) => (
                    <div key={index} className="tourist-place-card">
                      {place.image && (
                        <div className="tourist-place-image">
                          <img 
                            src={URL.createObjectURL(place.image)} 
                            alt={place.name}
                          />
                        </div>
                      )}
                      <div className="tourist-place-info">
                        <strong>{place.name}</strong>
                        {(place.city || place.state) && (
                          <span className="place-distance">
                            {place.city}{place.city && place.state ? ', ' : ''}{place.state}
                          </span>
                        )}
                        {place.distance != null && place.distance !== '' && (
                          <span className="place-distance">{place.distance} km away</span>
                        )}
                        {place.description && <p className="place-description">{place.description}</p>}
                      </div>
                      <button
                        type="button"
                        className="remove-tourist-place"
                        onClick={() => handleRemoveTouristPlace(index)}
                        title="Remove tourist place"
                      >
                        <FaTimes />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              {showAddTouristPlace ? (
                <div className="add-tourist-place-form">
                  <div className="form-group">
                    <label>Tourist Place Name *</label>
                    <input
                      type="text"
                      value={newTouristPlace.name}
                      onChange={(e) => setNewTouristPlace(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g., Gateway of India"
                      autoFocus
                    />
                  </div>
                  <div className="form-group">
                    <label>City *</label>
                    <input
                      type="text"
                      value={newTouristPlace.city}
                      onChange={(e) => setNewTouristPlace(prev => ({ ...prev, city: e.target.value }))}
                      placeholder="e.g., Mumbai"
                    />
                  </div>
                  <div className="form-group">
                    <label>State *</label>
                    <input
                      type="text"
                      value={newTouristPlace.state}
                      onChange={(e) => setNewTouristPlace(prev => ({ ...prev, state: e.target.value }))}
                      placeholder="e.g., Maharashtra"
                    />
                  </div>
                  <div className="form-group">
                    <label>Distance (km)</label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      value={newTouristPlace.distance}
                      onChange={(e) => setNewTouristPlace(prev => ({ ...prev, distance: e.target.value }))}
                      placeholder="e.g., 5"
                    />
                  </div>
                  <div className="form-group">
                    <label>Description</label>
                    <textarea
                      value={newTouristPlace.description}
                      onChange={(e) => setNewTouristPlace(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Brief description of the tourist place"
                      rows={3}
                    />
                  </div>
                  <div className="form-group">
                    <label>Upload Image</label>
                    <div className="tourist-image-upload">
                      {newTouristPlace.image ? (
                        <div className="tourist-image-preview">
                          <img 
                            src={URL.createObjectURL(newTouristPlace.image)} 
                            alt="Preview"
                          />
                          <button
                            type="button"
                            className="remove-image"
                            onClick={() => setNewTouristPlace(prev => ({ ...prev, image: null }))}
                          >
                            <FaTimes />
                          </button>
                        </div>
                      ) : (
                        <label className="tourist-image-upload-btn">
                          <FaCamera />
                          <span>Choose Image</span>
                          <input
                            type="file"
                            accept="image/jpeg,image/jpg,image/png,image/webp"
                            onChange={handleTouristPlaceImageSelect}
                            style={{ display: 'none' }}
                          />
                        </label>
                      )}
                    </div>
                  </div>
                  <div className="add-amenity-buttons">
                    <button
                      type="button"
                      className="btn-confirm-amenity"
                      onClick={handleAddTouristPlace}
                    >
                      Add Tourist Place
                    </button>
                    <button
                      type="button"
                      className="btn-cancel-amenity"
                      onClick={() => {
                        setShowAddTouristPlace(false);
                        setNewTouristPlace({ name: '', city: '', state: '', distance: '', description: '', image: null });
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  className="btn-add-more"
                  onClick={() => setShowAddTouristPlace(true)}
                >
                  <FaPlus />
                  Add Tourist Attraction
                </button>
              )}
            </div>

            {/* Policy Section */}
            <div className="form-section">
              <h3>Property Policies</h3>
              <p className="section-description">Set cancellation and payment policies for your property</p>
              
              <div className="form-group">
                <label htmlFor="cancellationPolicy">
                  Cancellation Policy <span className="required">*</span>
                </label>
                <div className="custom-select-wrapper" style={{ position: 'relative' }}>
                  <button
                    type="button"
                    ref={cancellationBtnRef}
                    className="custom-select-trigger"
                    onClick={() => setShowCancellationDropdown(v => !v)}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      background: 'white',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      cursor: 'pointer',
                      fontSize: '14px',
                      color: '#1f2937',
                      textAlign: 'left'
                    }}
                  >
                    <span>{formData.cancellationPolicy}</span>
                    {showCancellationDropdown ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
                  </button>
                  {showCancellationDropdown && (
                    <div
                      className="custom-select-options"
                      style={{
                        position: 'fixed',
                        top: cancellationBtnRef.current
                          ? cancellationBtnRef.current.getBoundingClientRect().bottom + 4
                          : 'auto',
                        left: cancellationBtnRef.current
                          ? cancellationBtnRef.current.getBoundingClientRect().left
                          : 'auto',
                        width: cancellationBtnRef.current
                          ? cancellationBtnRef.current.getBoundingClientRect().width
                          : 320,
                        zIndex: 99999,
                        background: 'white',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                        maxHeight: '240px',
                        overflowY: 'auto'
                      }}
                    >
                      {[
                        'Free cancellation up to 24 hours before check-in',
                        'Free cancellation up to 48 hours before check-in',
                        'Free cancellation up to 7 days before check-in',
                        'Non-refundable',
                        'Flexible - Full refund 1 day prior to arrival',
                        'Moderate - Full refund 5 days prior to arrival',
                        'Strict - 50% refund up to 1 week prior to arrival'
                      ].map(option => (
                        <div
                          key={option}
                          onClick={() => {
                            setFormData({ ...formData, cancellationPolicy: option });
                            setShowCancellationDropdown(false);
                          }}
                          style={{
                            padding: '10px 14px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            background: formData.cancellationPolicy === option ? '#eff6ff' : 'white',
                            color: formData.cancellationPolicy === option ? '#2563eb' : '#1f2937'
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = '#f3f4f6'}
                          onMouseLeave={e => e.currentTarget.style.background = formData.cancellationPolicy === option ? '#eff6ff' : 'white'}
                        >
                          {formData.cancellationPolicy === option && <FaCheck size={11} />}
                          {option}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="paymentPolicy">
                  Payment Policy <span className="required">*</span>
                </label>
                <div className="custom-select-wrapper" style={{ position: 'relative' }}>
                  <button
                    type="button"
                    ref={paymentBtnRef}
                    className="custom-select-trigger"
                    onClick={() => setShowPaymentDropdown(v => !v)}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      background: 'white',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      cursor: 'pointer',
                      fontSize: '14px',
                      color: '#1f2937',
                      textAlign: 'left'
                    }}
                  >
                    <span>{formData.paymentPolicy}</span>
                    {showPaymentDropdown ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
                  </button>
                  {showPaymentDropdown && (
                    <div
                      className="custom-select-options"
                      style={{
                        position: 'fixed',
                        top: paymentBtnRef.current
                          ? paymentBtnRef.current.getBoundingClientRect().bottom + 4
                          : 'auto',
                        left: paymentBtnRef.current
                          ? paymentBtnRef.current.getBoundingClientRect().left
                          : 'auto',
                        width: paymentBtnRef.current
                          ? paymentBtnRef.current.getBoundingClientRect().width
                          : 320,
                        zIndex: 99999,
                        background: 'white',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                        maxHeight: '240px',
                        overflowY: 'auto'
                      }}
                    >
                      {[
                        'Pay at property',
                        'Pay online (full amount)',
                        'Pay 50% now, 50% at property',
                        'Pay online with free cancellation',
                        'Advance payment required'
                      ].map(option => (
                        <div
                          key={option}
                          onClick={() => {
                            setFormData({ ...formData, paymentPolicy: option });
                            setShowPaymentDropdown(false);
                          }}
                          style={{
                            padding: '10px 14px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            background: formData.paymentPolicy === option ? '#eff6ff' : 'white',
                            color: formData.paymentPolicy === option ? '#2563eb' : '#1f2937'
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = '#f3f4f6'}
                          onMouseLeave={e => e.currentTarget.style.background = formData.paymentPolicy === option ? '#eff6ff' : 'white'}
                        >
                          {formData.paymentPolicy === option && <FaCheck size={11} />}
                          {option}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="additionalNotes">
                  Additional Policy Notes (Optional)
                </label>
                <textarea
                  id="additionalNotes"
                  value={formData.additionalNotes}
                  onChange={(e) => setFormData({ ...formData, additionalNotes: e.target.value })}
                  placeholder="Add any additional policy information, special conditions, or important notes for guests..."
                  rows="4"
                />
              </div>
            </div>

            {/* Discount/Offer Section */}
            <div className="form-section">
              <h3>🎯 Exclusive Offers & Discounts (Optional)</h3>
              <p className="section-description">Create special offers to attract more guests and increase bookings</p>
              
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.discountIsActive}
                    onChange={(e) => setFormData({ ...formData, discountIsActive: e.target.checked })}
                  />
                  <span>Enable Special Offer/Discount</span>
                </label>
              </div>

              {formData.discountIsActive && (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="discountType">
                        Discount Type <span className="required">*</span>
                      </label>
                      <select
                        id="discountType"
                        value={formData.discountType}
                        onChange={(e) => setFormData({ ...formData, discountType: e.target.value })}
                        required
                      >
                        <option value="percentage">Percentage Discount</option>
                        <option value="fixed">Fixed Amount Discount</option>
                        <option value="early_bird">Early Bird Special</option>
                        <option value="last_minute">Last Minute Deal</option>
                        <option value="seasonal">Seasonal Offer</option>
                        <option value="member_deal">Member Deal</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label htmlFor="discountValue">
                        Discount Value <span className="required">*</span>
                      </label>
                      <input
                        type="number"
                        id="discountValue"
                        min="0"
                        max={formData.discountType === 'percentage' ? '100' : undefined}
                        step={formData.discountType === 'percentage' ? '1' : '50'}
                        value={formData.discountValue}
                        onChange={(e) => setFormData({ ...formData, discountValue: e.target.value })}
                        placeholder={formData.discountType === 'percentage' ? 'e.g., 20' : 'e.g., 500'}
                        required
                      />
                      <small className="field-hint">
                        {formData.discountType === 'percentage' ? '% off original price' : '₹ off original price'}
                      </small>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="discountTitle">
                        Offer Title <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        id="discountTitle"
                        value={formData.discountTitle}
                        onChange={(e) => setFormData({ ...formData, discountTitle: e.target.value })}
                        placeholder="e.g., Weekend Special, Early Bird Discount"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="discountBadge">
                        Display Badge
                      </label>
                      <input
                        type="text"
                        id="discountBadge"
                        value={formData.discountBadge}
                        onChange={(e) => setFormData({ ...formData, discountBadge: e.target.value })}
                        placeholder="e.g., Special Offer, Limited Time"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="discountDescription">
                      Offer Description
                    </label>
                    <textarea
                      id="discountDescription"
                      value={formData.discountDescription}
                      onChange={(e) => setFormData({ ...formData, discountDescription: e.target.value })}
                      placeholder="Describe your special offer to attract guests..."
                      rows="3"
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="discountValidFrom">
                        Valid From
                      </label>
                      <input
                        type="date"
                        id="discountValidFrom"
                        value={formData.discountValidFrom}
                        onChange={(e) => setFormData({ ...formData, discountValidFrom: e.target.value })}
                        min={new Date().toISOString().split('T')[0]}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="discountValidUntil">
                        Valid Until
                      </label>
                      <input
                        type="date"
                        id="discountValidUntil"
                        value={formData.discountValidUntil}
                        onChange={(e) => setFormData({ ...formData, discountValidUntil: e.target.value })}
                        min={formData.discountValidFrom || new Date().toISOString().split('T')[0]}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="discountMinStayNights">
                        Minimum Stay (Nights)
                      </label>
                      <input
                        type="number"
                        id="discountMinStayNights"
                        min="1"
                        value={formData.discountMinStayNights}
                        onChange={(e) => setFormData({ ...formData, discountMinStayNights: parseInt(e.target.value) || 1 })}
                      />
                    </div>

                    {formData.discountType === 'percentage' && (
                      <div className="form-group">
                        <label htmlFor="discountMaxDiscountAmount">
                          Max Discount Amount (₹)
                        </label>
                        <input
                          type="number"
                          id="discountMaxDiscountAmount"
                          min="0"
                          step="50"
                          value={formData.discountMaxDiscountAmount}
                          onChange={(e) => setFormData({ ...formData, discountMaxDiscountAmount: e.target.value })}
                          placeholder="e.g., 2000"
                        />
                        <small className="field-hint">
                          Maximum discount amount for percentage discounts
                        </small>
                      </div>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="discountTermsAndConditions">
                      Terms & Conditions
                    </label>
                    <textarea
                      id="discountTermsAndConditions"
                      value={formData.discountTermsAndConditions}
                      onChange={(e) => setFormData({ ...formData, discountTermsAndConditions: e.target.value })}
                      placeholder="Add any terms and conditions for this offer..."
                      rows="3"
                    />
                  </div>

                  {/* Discount Preview */}
                  {formData.discountValue && formData.pricePerNight && (
                    <div className="discount-preview">
                      <h4>Offer Preview</h4>
                      <div className="preview-card">
                        <div className="preview-badge">{formData.discountBadge}</div>
                        <div className="preview-title">{formData.discountTitle || 'Special Offer'}</div>
                        <div className="preview-pricing">
                          <span className="original-price">₹{parseFloat(formData.pricePerNight).toLocaleString()}</span>
                          <span className="discounted-price">
                            ₹{(() => {
                              const originalPrice = parseFloat(formData.pricePerNight);
                              const discountValue = parseFloat(formData.discountValue);
                              let discountedPrice;
                              
                              if (formData.discountType === 'percentage') {
                                const discountAmount = (originalPrice * discountValue) / 100;
                                const maxDiscount = formData.discountMaxDiscountAmount ? parseFloat(formData.discountMaxDiscountAmount) : Infinity;
                                const finalDiscountAmount = Math.min(discountAmount, maxDiscount);
                                discountedPrice = originalPrice - finalDiscountAmount;
                              } else {
                                discountedPrice = Math.max(0, originalPrice - discountValue);
                              }
                              
                              return Math.round(discountedPrice).toLocaleString();
                            })()}
                          </span>
                          <span className="savings">
                            Save {formData.discountType === 'percentage' ? `${formData.discountValue}%` : `₹${formData.discountValue}`}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="modal-actions">
              <button
                type="button"
                className="btn-cancel"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-submit"
                disabled={loading || uploadingImages}
              >
                {loading || uploadingImages ? 'Creating...' : 'Create Property'}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default AddPropertyModal;
